import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import * as CryptoJS from 'crypto-js';
import { getDepreciationBatches, deleteDepreciationBatch, getDepreciationBatchDetails } from '../../../services/asset.service';
import { getCompanyProfileDDList } from '../../../services/user.service';
import './UploadDepreciation.css';
import '../AssetInfo/AssetList.css';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';

const monthNames = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
];

function RemoveDepreciationLog() {
    const [batches, setBatches] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    // Filter states
    const [selectedCompany, setSelectedCompany] = useState('');
    const [selectedType, setSelectedType] = useState('All');
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('All');

    // Pagination states
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalItems, setTotalItems] = useState(0);

    // Deletion states
    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [confirmText, setConfirmText] = useState('');

    // Safeguard detail states
    const [detailedLoading, setDetailedLoading] = useState(false);
    const [isAuthorized, setIsAuthorized] = useState(true);
    const [assetCount, setAssetCount] = useState(0);
    const [totalDepreciation, setTotalDepreciation] = useState(0);
    const [batchStatus, setBatchStatus] = useState('Success');
    const [glStatus, setGlStatus] = useState('Not Posted');
    const [hasDependency, setHasDependency] = useState(false);
    const [siblingBatch, setSiblingBatch] = useState(null);
    const [deleteType, setDeleteType] = useState('');
    const [deletionReason, setDeletionReason] = useState('');
    const [generatedBy, setGeneratedBy] = useState('System');
    const [generatedOn, setGeneratedOn] = useState(null);

    const fetchBatches = () => {
        setLoading(true);
        const payload = {
            company_id: selectedCompany || null,
            batch_type: selectedType === 'All' ? null : selectedType,
            year: selectedYear || null,
            month: selectedMonth || null,
            status: selectedStatus === 'All' ? null : selectedStatus,
            search: search.trim() || null,
            page: page,
            limit: pageSize
        };
        getDepreciationBatches(payload)
            .then(res => {
                if (res.data && res.data.success) {
                    setBatches(res.data.data);
                    setTotalItems(res.data.total || 0);
                } else {
                    setErrorMsg(res.data.message || 'Failed to load depreciation logs');
                }
                setLoading(false);
            })
            .catch(err => {
                setErrorMsg(err.message || 'Error fetching depreciation logs');
                setLoading(false);
            });
    };

    const handleSearch = () => {
        if (page === 1) {
            fetchBatches();
        } else {
            setPage(1);
        }
    };

    // When page or pageSize changes, query data
    useEffect(() => {
        fetchBatches();
    }, [page, pageSize]);

    useEffect(() => {
        // Fetch companies
        const EncryptData = CryptoJS.AES.encrypt(
            JSON.stringify({
                authToken: sessionStorage.getItem('LoggedInToken'),
            }),
            process.env.REACT_APP_ENCRYPT_SECRET_KEY
        ).toString();

        getCompanyProfileDDList({ token: EncryptData })
            .then(res => {
                if (res.data) {
                    const companyList = res.data.oResult?.data || res.data.data || [];
                    setCompanies(companyList);
                }
            })
            .catch(err => {
                console.error("Error fetching companies:", err);
            });
    }, []);

    // Map batch_type to dropdown delete type key
    const getDefaultDeleteType = (batchType) => {
        if (batchType === 'I-GAAP AS') return 'AS';
        if (batchType === 'IndAS') return 'IndAS';
        if (batchType === 'Income Tax') return 'IT';
        return 'AS';
    };

    const handleDeleteClick = (batch) => {
        setSelectedBatch(batch);
        setConfirmText('');
        setDeletionReason('');
        setDeleteType(getDefaultDeleteType(batch.batch_type));
        setDetailedLoading(true);
        setIsAuthorized(true);
        setHasDependency(false);
        setSiblingBatch(null);
        setOpenConfirm(true);

        const payload = {
            batch_id: batch.batch_id,
            batch_type: batch.batch_type,
            company_id: batch.company_id,
            year: batch.year,
            month: batch.month
        };

        getDepreciationBatchDetails(payload)
            .then(res => {
                if (res.data && res.data.success) {
                    const data = res.data.data;
                    setIsAuthorized(data.is_authorized);
                    setAssetCount(data.asset_count);
                    setTotalDepreciation(data.total_depreciation);
                    setBatchStatus(data.batch_status);
                    setGlStatus(data.gl_status);
                    setHasDependency(data.has_dependency);
                    setSiblingBatch(data.sibling_batch);
                    setGeneratedBy(data.generated_by);
                    setGeneratedOn(data.generated_on);
                } else {
                    setErrorMsg(res.data.message || 'Failed to fetch batch details.');
                }
                setDetailedLoading(false);
            })
            .catch(err => {
                setErrorMsg(err.message || 'Error loading batch metadata.');
                setDetailedLoading(false);
            });
    };

    const handleConfirmDelete = () => {
        if (!selectedBatch) return;
        setOpenConfirm(false);
        setLoading(true);
        setErrorMsg('');
        setSuccessMsg('');

        const payload = {
            batch_id: selectedBatch.batch_id,
            delete_type: deleteType,
            deletion_reason: deletionReason
        };

        deleteDepreciationBatch(payload)
            .then(res => {
                if (res.data && res.data.success) {
                    setSuccessMsg(res.data.message || 'Batch soft-delete complete (marked as REVERSED).');
                    fetchBatches();
                } else {
                    setErrorMsg(res.data.message || 'Delete failed.');
                    setLoading(false);
                }
            })
            .catch(err => {
                setErrorMsg(err.message || 'Error deleting batch.');
                setLoading(false);
            });
    };

    const getMonthLabel = (monthNum) => {
        const found = monthNames.find(m => m.value === Number(monthNum));
        return found ? found.label : monthNum;
    };

    const currentYear = new Date().getFullYear();
    const uniqueYears = [];
    for (let y = currentYear + 1; y >= 2020; y--) {
        uniqueYears.push(y);
    }

    const filteredBatches = batches;

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 2
        }).format(val || 0);
    };

    return (
        <div className="al-page">
            <div className="al-page-header">
                <div className="al-page-header-left">
                    <div className="al-page-header-icon" style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>
                        <i className="bi bi-trash3-fill" />
                    </div>
                    <div className="al-page-header-text">
                        <h5 className="al-page-title">Remove Depreciation</h5>
                        <p className="al-page-subtitle">Revert asset depreciation updates by selecting and removing a batch</p>
                    </div>
                </div>
                {!loading && (
                    <div className="al-record-badge">
                        <i className="bi bi-layers-fill" />
                        <strong>{totalItems.toLocaleString('en-IN')}</strong>&nbsp;Total Records
                    </div>
                )}
            </div>

            {successMsg && (
                <div className="ud-alert ud-alert-success" role="alert">
                    <i className="bi bi-check-circle-fill" /> {successMsg}
                </div>
            )}
            {errorMsg && (
                <div className="ud-alert ud-alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle-fill" /> {errorMsg}
                </div>
            )}

            {/* Filter Panel */}
            <div className="al-filter-card">
                <div className="al-filter-card-header">
                    <span className="al-filter-card-title">
                        <i className="bi bi-funnel-fill" /> Filter Batches
                    </span>
                </div>
                <div className="al-filter-card-body">
                    <div className="al-filter-grid">
                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-building" /> Company
                            </label>
                            <select
                                className="al-form-select"
                                value={selectedCompany}
                                onChange={(e) => setSelectedCompany(e.target.value)}
                            >
                                <option value="">All Companies</option>
                                {companies.map(c => (
                                    <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-tag" /> Type
                            </label>
                            <select
                                className="al-form-select"
                                value={selectedType}
                                onChange={(e) => setSelectedType(e.target.value)}
                            >
                                <option value="All">All Types</option>
                                <option value="I-GAAP AS">I-GAAP AS</option>
                                <option value="IndAS">IndAS</option>
                                <option value="Income Tax">Income Tax</option>
                            </select>
                        </div>

                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-calendar" /> Financial Year
                            </label>
                            <select
                                className="al-form-select"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                            >
                                <option value="">All Years</option>
                                {uniqueYears.map(yr => (
                                    <option key={yr} value={yr}>{yr}</option>
                                ))}
                            </select>
                        </div>

                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-calendar-month" /> Month
                            </label>
                            <select
                                className="al-form-select"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            >
                                <option value="">All Months</option>
                                {monthNames.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-flag" /> Status
                            </label>
                            <select
                                className="al-form-select"
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                            >
                                <option value="All">All Statuses</option>
                                <option value="Uploaded">Uploaded</option>
                                <option value="Generated">Generated</option>
                            </select>
                        </div>

                        <div className="al-filter-group">
                            <label className="al-filter-label">
                                <i className="bi bi-search" /> Search
                            </label>
                            <input
                                type="text"
                                className="al-form-input"
                                placeholder="Search ID, File..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearch();
                                }}
                            />
                        </div>
                    </div>

                    <div className="al-filter-actions">
                        <button className="al-btn al-btn-primary" onClick={handleSearch}>
                            <i className="bi bi-search" /> Search Batches
                        </button>
                    </div>
                </div>
            </div>

            <div className="al-grid-card">
                <div className="al-grid-card-header">
                    <span className="al-grid-card-title">
                        <i className="bi bi-list-task" /> Depreciation Batches Log History
                    </span>
                    {!loading && batches.length > 0 && (
                        <span className="al-showing-badge">
                            Showing <strong>{batches.length}</strong> of <strong>{totalItems.toLocaleString('en-IN')}</strong>
                        </span>
                    )}
                </div>

                <div className="al-filter-card-body" style={{ padding: '20px' }}>

                    <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <Table aria-label="batches table">
                            <TableHead style={{ backgroundColor: '#f9fafb' }}>
                                <TableRow>
                                    <TableCell style={{ fontWeight: '600', color: '#374151' }}>Batch ID</TableCell>
                                    <TableCell style={{ fontWeight: '600', color: '#374151' }}>Company Name</TableCell>
                                    <TableCell style={{ fontWeight: '600', color: '#374151' }}>Type</TableCell>
                                    <TableCell style={{ fontWeight: '600', color: '#374151' }}>Period</TableCell>
                                    <TableCell align="right" style={{ fontWeight: '600', color: '#374151' }}>Asset Count</TableCell>
                                    <TableCell align="center" style={{ fontWeight: '600', color: '#374151' }}>Source</TableCell>
                                    <TableCell align="center" style={{ fontWeight: '600', color: '#374151' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && filteredBatches.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" style={{ padding: '40px 0', color: '#6b7280' }}>
                                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                                            Loading depreciation batches...
                                        </TableCell>
                                    </TableRow>
                                ) : filteredBatches.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" style={{ padding: '40px 0', color: '#6b7280' }}>
                                            No batches match current filters.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredBatches.map((row) => (
                                        <TableRow key={row.batch_id + '-' + row.batch_type} hover style={{ transition: 'background-color 0.2s' }}>
                                            <TableCell style={{ fontWeight: '500', color: '#111827' }}>
                                                <code>{row.batch_id}</code>
                                            </TableCell>
                                            <TableCell style={{ color: '#4b5563' }}>{row.company_name || 'N/A'}</TableCell>
                                            <TableCell style={{ fontWeight: '500' }}>{row.batch_type}</TableCell>
                                            <TableCell>{getMonthLabel(row.month)} - {row.year}</TableCell>
                                            <TableCell align="right" style={{ fontWeight: '600' }}>{row.asset_count}</TableCell>
                                            <TableCell align="center">
                                                <span style={{
                                                    padding: '4px 10px',
                                                    borderRadius: '12px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600',
                                                    backgroundColor: row.status === 'Uploaded' ? '#e7f3ff' : '#e2f9e9',
                                                    color: row.status === 'Uploaded' ? '#0d6efd' : '#198754',
                                                    border: `1px solid ${row.status === 'Uploaded' ? '#bcd9f0' : '#c3e6cb'}`
                                                }}>
                                                    {row.status}
                                                </span>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleDeleteClick(row)}
                                                    startIcon={<i className="bi bi-trash3" />}
                                                    style={{ textTransform: 'none', borderRadius: '6px' }}
                                                >
                                                    Remove
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination Bar */}
                    <div className="al-pagination-bar">
                        <div className="al-page-size-label">
                            {totalItems > 0 ? (
                                `Showing ${Math.min((page - 1) * pageSize + 1, totalItems)} to ${Math.min(page * pageSize, totalItems)} of ${totalItems} entries`
                            ) : (
                                "Showing 0 to 0 of 0 entries"
                            )}
                        </div>
                        <div className="al-page-size-row" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Pagination
                                count={Math.ceil(totalItems / pageSize) || 1}
                                page={page}
                                onChange={(event, value) => setPage(value)}
                                color="primary"
                                size="small"
                                className="al-pagination"
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className="al-page-size-label">Page Size:</span>
                                <select
                                    className="al-form-select"
                                    style={{ width: '80px', height: '32px', padding: '0 8px', backgroundColor: '#fff' }}
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setPage(1);
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    style: { borderRadius: '12px', padding: '12px', maxWidth: '580px', width: '100%' }
                }}
            >
                <DialogTitle id="alert-dialog-title" style={{ fontWeight: '700', color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="bi bi-exclamation-triangle-fill" /> Safe Batch Deletion Workflow
                </DialogTitle>
                <DialogContent>
                    {detailedLoading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: '16px' }}>
                            <CircularProgress size={40} style={{ color: '#dc2626' }} />
                            <DialogContentText style={{ fontSize: '0.9rem', color: '#4b5563' }}>Retrieving batch details, verifying dependencies and role rights...</DialogContentText>
                        </div>
                    ) : (
                        <>
                            {!isAuthorized && (
                                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '16px', marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                    <i className="bi bi-x-circle-fill" style={{ color: '#dc2626', fontSize: '1.25rem', marginTop: '2px' }} />
                                    <div>
                                        <h6 style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#991b1b', fontSize: '0.9rem' }}>Access Resticted</h6>
                                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#b91c1c' }}>
                                            Only users holding "EC Head", "Finance Head", or "Finance" roles possess privileges to execute batch deletions.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {hasDependency && (
                                <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '8px', padding: '16px', marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                    <i className="bi bi-exclamation-triangle-fill" style={{ color: '#d97706', fontSize: '1.25rem', marginTop: '2px' }} />
                                    <div>
                                        <h6 style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#92400e', fontSize: '0.9rem' }}>Downstream Dependency Conflict</h6>
                                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#b45309' }}>
                                            A subsequent depreciation batch exists in a later period. Reverting this batch would break WDV calculations. Deletion is blocked.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <DialogContentText id="alert-dialog-description" style={{ color: '#1f2937', marginBottom: '16px', fontSize: '0.9rem' }}>
                                Reverting depreciation update for batch ID <code>{selectedBatch?.batch_id}</code>:
                            </DialogContentText>

                            {selectedBatch && (
                                <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                                    <h6 style={{ margin: '0 0 12px 0', fontWeight: '700', color: '#374151', fontSize: '0.875rem' }}>Blast Radius:</h6>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '8px 16px', fontSize: '0.85rem' }}>
                                        <div style={{ color: '#6b7280' }}>Company:</div>
                                        <div style={{ fontWeight: '500' }}>{selectedBatch.company_name}</div>

                                        <div style={{ color: '#6b7280' }}>Standard Style / Type:</div>
                                        <div style={{ fontWeight: '500' }}>{selectedBatch.batch_type}</div>

                                        <div style={{ color: '#6b7280' }}>Period Range:</div>
                                        <div style={{ fontWeight: '500' }}>{getMonthLabel(selectedBatch.month)} - {selectedBatch.year}</div>

                                        <div style={{ color: '#6b7280' }}>Assets Impacted:</div>
                                        <div style={{ fontWeight: '700', color: '#b91c1c' }}>{assetCount}</div>

                                        <div style={{ color: '#6b7280' }}>Total Depreciation Reversal:</div>
                                        <div style={{ fontWeight: '700', color: '#b91c1c' }}>{formatCurrency(totalDepreciation)}</div>

                                        <div style={{ color: '#6b7280' }}>Batch Process Quality:</div>
                                        <div style={{ fontWeight: '500' }}>{batchStatus}</div>

                                        <div style={{ color: '#6b7280' }}>General Ledger Posting:</div>
                                        <div style={{ fontWeight: '500', color: '#10b981' }}>{glStatus}</div>

                                        <div style={{ color: '#6b7280' }}>Creator Metadata:</div>
                                        <div style={{ fontWeight: '500', fontSize: '0.8rem' }}>
                                            {generatedBy} ({generatedOn ? new Date(generatedOn).toLocaleDateString() : 'N/A'})
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div style={{
                                backgroundColor: '#fef3c7',
                                border: '1px solid #fcd34d',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                marginBottom: '16px'
                            }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#92400e', textTransform: 'uppercase' }}>
                                        Select Depreciation Type to Delete
                                    </label>
                                    <select
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #fbbf24',
                                            borderRadius: '6px',
                                            backgroundColor: '#ffffff',
                                            fontSize: '0.875rem',
                                            color: '#78350f',
                                            fontWeight: '500',
                                            outline: 'none',
                                            cursor: 'pointer'
                                        }}
                                        value={deleteType}
                                        onChange={(e) => setDeleteType(e.target.value)}
                                        disabled={detailedLoading}
                                    >
                                        <option value="AS">AS (I-GAAP)</option>
                                        <option value="IndAS">IndAS</option>
                                        <option value="IT">IT (Income Tax)</option>
                                    </select>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: '#92400e' }}>
                                        Data will be deleted from: <strong>
                                            {deleteType === 'AS' ? 'company_igaap_depreciation' :
                                                deleteType === 'IndAS' ? 'company_indas_depreciation' :
                                                    deleteType === 'IT' ? 'company_it_depreciation' : '—'}
                                        </strong>
                                    </p>
                                </div>
                            </div>

                            <TextField
                                label="Reason for Deletion"
                                placeholder="State why this batch needs correction (Required for Audit Trail)"
                                multiline
                                rows={2}
                                fullWidth
                                variant="outlined"
                                value={deletionReason}
                                onChange={(e) => setDeletionReason(e.target.value)}
                                style={{ marginBottom: '16px' }}
                                disabled={detailedLoading}
                                InputLabelProps={{ shrink: true }}
                            />

                            <DialogContentText style={{ color: '#1f2937', fontWeight: '500', fontSize: '0.875rem', marginBottom: '8px' }}>
                                Please type the exact Batch ID <code>{selectedBatch?.batch_id}</code> to confirm:
                            </DialogContentText>
                            <TextField
                                size="small"
                                fullWidth
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="Type Batch ID here"
                                variant="outlined"
                                disabled={detailedLoading}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '16px 24px' }}>
                    <Button onClick={() => setOpenConfirm(false)} style={{ textTransform: 'none', color: '#4b5563' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={
                            detailedLoading ||
                            !isAuthorized ||
                            hasDependency ||
                            !deletionReason.trim() ||
                            confirmText !== selectedBatch?.batch_id
                        }
                        style={{
                            textTransform: 'none',
                            borderRadius: '6px',
                            backgroundColor:
                                (!detailedLoading &&
                                    isAuthorized &&
                                    !hasDependency &&
                                    deletionReason.trim() &&
                                    confirmText === selectedBatch?.batch_id)
                                    ? '#dc2626'
                                    : '#f3f4f6',
                            color:
                                (!detailedLoading &&
                                    isAuthorized &&
                                    !hasDependency &&
                                    deletionReason.trim() &&
                                    confirmText === selectedBatch?.batch_id)
                                    ? '#ffffff'
                                    : '#9ca3af'
                        }}
                    >
                        Confirm Reversal
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default RemoveDepreciationLog;
